import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  TextInput,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { FormattedMessage } from '@/components/chat/FormattedMessage';
import { StockEventsPanel } from '@/components/stock/StockEventsPanel';
import { perplexityService } from '@/services/api/perplexity.service';
import { useStockEvents } from '@/hooks/useStockEvents';
// Chart removed from ChatTab; users can use the Chart tab
import {
  appendMessageToHistory,
  saveSearchHistory,
  getFullStockConversationHistory,
} from '@/services/history/searchHistoryService';
import { Message } from '@/types/search-history.types';

interface ChatTabProps {
  ticker: string;
  companyName: string;
  initialQuery?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  citations?: string[];
  timestamp: Date;
}

/**
 * ChatTab - Complete stock chat implementation for use within StockAnalysisScreen
 * Includes conversation history, AI responses, stock chart, and events
 */
export const ChatTab: React.FC<ChatTabProps> = ({ ticker, companyName, initialQuery }) => {
  // Chart removed from ChatTab; keep chat-only state

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // History loading state
  const [historyLoading, setHistoryLoading] = useState(true);
  const [previousMessages, setPreviousMessages] = useState<any[] | undefined>(undefined);

  // Pull-to-refresh state
  const [refreshing, setRefreshing] = useState(false);

  const scrollViewRef = useRef<ScrollView>(null);

  // Stock events
  const {
    events,
    isLoading: eventsLoading,
    error: eventsError,
    refreshEvents,
  } = useStockEvents(ticker, companyName);

  /**
   * Load conversation history from database
   */
  useEffect(() => {
    const loadHistory = async () => {
      setHistoryLoading(true);
      console.log('📜 Loading conversation history for', ticker);

      const historyResult = await getFullStockConversationHistory(ticker);

      if (historyResult.success && historyResult.messages && historyResult.messages.length > 0) {
        console.log('✅ Loaded', historyResult.messages.length, 'previous messages');
        setPreviousMessages(historyResult.messages);
      } else {
        console.log('ℹ️ No previous conversation history found');
        setPreviousMessages(undefined);
      }

      setHistoryLoading(false);
    };

    loadHistory();
  }, [ticker]);

  /**
   * Build AI context with smart truncation
   * Only sends last 3-5 messages to save tokens
   */
  const MAX_TURNS = 8;

  /**
   * Handle pull-to-refresh
   */
  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshEvents();
    } finally {
      setRefreshing(false);
    }
  };

  // No chart data in ChatTab; events refresh is handled via hook

  // Load previous messages if available
  useEffect(() => {
    if (previousMessages && previousMessages.length > 0) {
      console.log('📜 Loading previous messages:', previousMessages.length);
      const convertedMessages: ChatMessage[] = [...previousMessages]
        .sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        .map((msg: any) => ({
          role: msg.role,
          content: msg.content,
          citations: msg.citations || [],
          timestamp: new Date(msg.timestamp),
        }));
      setMessages(convertedMessages);

      setTimeout(() => {
        console.log('📍 Auto-scrolling to latest message');
        scrollViewRef.current?.scrollToEnd({ animated: false });
      }, 500);
    }
  }, [previousMessages]);

  // Process initial query if provided
  const initialQueryProcessed = useRef(false);

  useEffect(() => {
    if (
      initialQuery &&
      !initialQueryProcessed.current &&
      (!previousMessages || previousMessages.length === 0) &&
      !historyLoading
    ) {
      console.log('📝 Processing initial query:', initialQuery);
      initialQueryProcessed.current = true;
      handleSendMessage(initialQuery);
    } else if (previousMessages && previousMessages.length > 0) {
      console.log('⏭️ Skipping initial query - previous conversation exists');
      initialQueryProcessed.current = true;
    }
  }, [initialQuery, previousMessages, historyLoading]);

  // Chart functions removed

  /**
   * Send message and get AI response
   */
  const handleSendMessage = async (text: string) => {
    console.log('🚀 handleSendMessage called with:', text);
    const trimmedText = text.trim();
    if (!trimmedText) {
      console.log('⚠️ Empty text, returning early');
      return;
    }
    console.log('✅ Processing message:', trimmedText);

    const userMessage: ChatMessage = {
      role: 'user',
      content: trimmedText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setAiLoading(true);
    setAiError(null);

    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);

    try {
      // Build structured context: system + optional summary + last-k
      const sys: Array<{ role: 'system'; content: string }> = [
        {
          role: 'system',
          content: `You are an equity research assistant focused on ${ticker} (${companyName}). Be factual and concise.`,
        },
      ];
      // Optional: a short derived summary from previousMessages (if present)
      if (previousMessages && previousMessages.length > 0) {
        const recent = previousMessages
          .slice(-12)
          .map(m => `${m.role}: ${m.content}`)
          .join('\n');
        sys.push({
          role: 'system',
          content: `Prior discussion summary hint (last 12 turns):\n${recent.slice(0, 1000)}`,
        });
      }
      const lastK = [
        ...messages.map(m => ({ role: m.role, content: m.content })),
        { role: 'user' as const, content: trimmedText },
      ].slice(-MAX_TURNS);
      const result = await perplexityService.chat([...sys, ...lastK] as any, 'week' as any);

      if (result.error) {
        console.error('❌ AI error:', result.error.message);
        setAiError(result.error.message);
      } else if (result.data) {
        console.log('✅ Got AI response:', result.data.content.substring(0, 100) + '...');
        const aiMessage: ChatMessage = {
          role: 'assistant',
          content: result.data.content,
          citations: result.data.citations,
          timestamp: new Date(),
        };

        setMessages(prev => [...prev, aiMessage]);

        const userMessageForDB: Message = {
          id: Date.now().toString(),
          role: 'user',
          content: trimmedText,
          timestamp: new Date().toISOString(),
        };

        const aiMessageForDB: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: result.data.content,
          timestamp: new Date().toISOString(),
          citations: result.data.citations,
        };

        if (messages.length === 0) {
          await saveSearchHistory({
            query: trimmedText,
            stock_symbol: ticker,
            stock_name: companyName,
            messages: [userMessageForDB, aiMessageForDB],
          });
        } else {
          await appendMessageToHistory(ticker, userMessageForDB);
          await appendMessageToHistory(ticker, aiMessageForDB);
        }

        console.log('✅ Saved messages to history for', ticker);

        setTimeout(() => {
          scrollViewRef.current?.scrollToEnd({ animated: true });
        }, 100);
      }
    } catch (error) {
      setAiError('Failed to get AI response');
      console.error('AI response error:', error);
    } finally {
      setAiLoading(false);
    }
  };

  /**
   * Render individual chat message
   */
  const renderMessage = (message: ChatMessage, index: number) => {
    const isUser = message.role === 'user';

    return (
      <View
        key={index}
        style={[styles.messageBubble, isUser ? styles.userBubble : styles.aiBubble]}
      >
        {isUser ? (
          <Text style={styles.userText}>{message.content}</Text>
        ) : (
          <FormattedMessage content={message.content} />
        )}

        {!isUser && message.citations && message.citations.length > 0 && (
          <View style={styles.citationsContainer}>
            <Text style={styles.citationsLabel}>Sources:</Text>
            {message.citations.slice(0, 3).map((citation, idx) => (
              <Text key={idx} style={styles.citationText} numberOfLines={1}>
                • {citation}
              </Text>
            ))}
          </View>
        )}

        <Text style={styles.timestamp}>
          {message.timestamp.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Text>
      </View>
    );
  };

  // Show loading while fetching history
  if (historyLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Colors.ai.primary} />
        <Text style={styles.loadingText}>Loading conversation history...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        style={styles.keyboardAvoid}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          ref={scrollViewRef}
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={Colors.ai.primary}
              colors={[Colors.ai.primary]}
            />
          }
        >
          {/* Previous Messages section removed; render all chat messages in one list below */}

          {/* Stock Chart removed from ChatTab; available in Chart tab */}

          {/* Stock Events Panel */}
          <StockEventsPanel
            ticker={ticker}
            companyName={companyName}
            events={events}
            isLoading={eventsLoading}
            error={eventsError}
            onRefresh={refreshEvents}
            defaultCollapsed={false}
          />

          {/* Chat Messages */}
          <View style={styles.chatSection}>
            {messages.length === 0 && !aiLoading ? (
              <View style={styles.emptyState}>
                <Ionicons name="chatbubbles-outline" size={48} color={Colors.text.tertiary} />
                <Text style={styles.emptyTitle}>Ask anything about {ticker}</Text>
                <Text style={styles.emptyText}>
                  Get AI-powered insights, earnings analysis, and market outlook
                </Text>
              </View>
            ) : (
              messages.map((message, index) => renderMessage(message, index))
            )}

            {aiLoading && (
              <View style={styles.aiLoadingBubble}>
                <ActivityIndicator size="small" color={Colors.ai.primary} />
                <Text style={styles.aiLoadingText}>Thinking...</Text>
              </View>
            )}

            {aiError && (
              <View style={styles.aiErrorContainer}>
                <Ionicons name="warning" size={16} color={Colors.status.error} />
                <Text style={styles.aiErrorText}>{aiError}</Text>
              </View>
            )}
          </View>
        </ScrollView>

        {/* Input Section */}
        <View style={styles.inputContainer}>
          <View style={styles.inputWrapper}>
            <Ionicons name="chatbubble-outline" size={20} color={Colors.text.tertiary} />
            <TextInput
              style={styles.input}
              placeholder={`Ask about ${ticker}...`}
              placeholderTextColor={Colors.text.tertiary}
              value={inputText}
              onChangeText={setInputText}
              onSubmitEditing={() => {
                console.log('⌨️ Submit via Enter key');
                handleSendMessage(inputText);
              }}
              editable={!aiLoading}
              multiline
              maxLength={500}
            />
            <TouchableOpacity
              style={[
                styles.sendButton,
                (!inputText.trim() || aiLoading) && styles.sendButtonDisabled,
              ]}
              onPress={() => {
                console.log('👆 Send button pressed');
                handleSendMessage(inputText);
              }}
              disabled={!inputText.trim() || aiLoading}
            >
              <Ionicons
                name="send"
                size={20}
                color={inputText.trim() && !aiLoading ? Colors.ai.primary : Colors.text.tertiary}
              />
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 16,
  },
  chartSection: {
    marginBottom: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  chartLoadingContainer: {
    padding: 40,
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: Colors.text.secondary,
  },
  errorContainer: {
    padding: 32,
    alignItems: 'center',
  },
  errorText: {
    marginTop: 12,
    fontSize: 14,
    color: Colors.status.error,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 16,
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: Colors.accent.primary,
    borderRadius: 8,
  },
  retryText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  chatSection: {
    paddingHorizontal: 16,
  },
  emptyState: {
    padding: 32,
    alignItems: 'center',
  },
  emptyTitle: {
    marginTop: 16,
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
    textAlign: 'center',
  },
  emptyText: {
    marginTop: 8,
    fontSize: 14,
    color: Colors.text.secondary,
    textAlign: 'center',
  },
  messageBubble: {
    maxWidth: '80%',
    marginBottom: 12,
    padding: 12,
    borderRadius: 16,
  },
  userBubble: {
    alignSelf: 'flex-end',
    backgroundColor: Colors.accent.primary,
  },
  aiBubble: {
    alignSelf: 'flex-start',
    backgroundColor: Colors.background.secondary,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  userText: {
    color: '#FFFFFF',
    fontSize: 15,
    lineHeight: 20,
  },
  citationsContainer: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
  },
  citationsLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: Colors.text.tertiary,
    marginBottom: 4,
  },
  citationText: {
    fontSize: 11,
    color: Colors.text.secondary,
    marginTop: 2,
  },
  timestamp: {
    fontSize: 10,
    color: Colors.text.tertiary,
    marginTop: 6,
    alignSelf: 'flex-end',
  },
  aiLoadingBubble: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    padding: 12,
    backgroundColor: Colors.background.secondary,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    gap: 8,
  },
  aiLoadingText: {
    fontSize: 14,
    color: Colors.ai.primary,
  },
  aiErrorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: `${Colors.status.error}15`,
    borderRadius: 8,
    gap: 8,
    marginTop: 8,
  },
  aiErrorText: {
    flex: 1,
    fontSize: 13,
    color: Colors.status.error,
  },
  inputContainer: {
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
    backgroundColor: Colors.background.secondary,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.background.tertiary,
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 12,
  },
  input: {
    flex: 1,
    fontSize: 15,
    color: Colors.text.primary,
    maxHeight: 100,
  },
  sendButton: {
    padding: 8,
  },
  sendButtonDisabled: {
    opacity: 0.5,
  },
  previousMessagesSection: {
    marginBottom: 16,
  },
  conversationLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginBottom: 12,
    gap: 6,
  },
  conversationLabelText: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.text.tertiary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  newConversationLabelText: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.ai.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
});
